import { Component, inject, Input, OnChanges, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Order } from '../../models/order.model';
import { TranslateService } from "@ngx-translate/core";
import { ProductOrder } from "../../models/product-order.model";
import { QualityError } from "../../models/quality-error.model";
import { FileHelper } from "../../helpers/file.helper";
import { ProcessingErrorEnum } from "../../enums/processing-error.enum";
import { Router } from "@angular/router";
import { OrganizationService } from "../../services/organization.service";
import { ScriptError } from "../../models/script-error.model";
import { Subscription } from "rxjs";
import { ScriptErrorService } from "../../services/script-error.service";

@Component({
    selector: 'app-files-errors',
    templateUrl: './files-errors.component.html',
    styleUrl: './files-errors.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FilesErrorsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() order: Order;
  @Input() product: ProductOrder;
  @Input() panelMode = false;
  selectedProduct: ProductOrder;
  translate = inject(TranslateService);
  private router = inject(Router);
  private organizationSub: Subscription;
  private scriptErrorService = inject(ScriptErrorService);
  private organizationService = inject(OrganizationService);
  protected readonly ProcessingErrorEnum = ProcessingErrorEnum;
  private scriptErrors: ScriptError[] = [];

  ngOnInit(): void {
    this.organizationSub = this.organizationService.organization$.subscribe(organization => {
      if (!organization) {
        return;
      }
      // TODO : This query raise an error for external organization users
      this.scriptErrorService.listSync(organization.id)
        .then(scriptErrors => this.scriptErrors = scriptErrors);
    });
  }

  ngOnChanges(): void {
    this.selectedProduct = this.product ? this.product : this.getProductsWithErrors()[0];
  }

  ngOnDestroy(): void {
    this.organizationSub?.unsubscribe();
  }

  /**
   * Get processing errors of a product
   * @param productOrder The product
   * @returns The list of processing errors
   */
  getErrors(productOrder: ProductOrder): QualityError[] {
    return this.order.qualityErrors.filter(item => {
      if (item.poId) {
        return item.poId === productOrder.poId;
      }
      const fileName = FileHelper.getFileName(this.order, productOrder);
      return item.file === fileName;
    });
  }

  /**
   * Get all products with files errors
   * @returns List of products
   */
  getProductsWithErrors(): ProductOrder[] {
    return this.order?.products.filter(product => {
      return this.getErrors(product).length;
    }) ?? [];
  }

  /**
   * Open proofs validation view
   * @returns Router navigate result
   */
  goToProofs(): Promise<boolean> {
    return this.router.navigate(
      [this.organizationService.currentOrganization.oid, 'bat', this.order.id]);
  }

  /**
   * Returns the error description for a given error code.
   * If a matching error code is not found,
   * the method returns the input error code itself.
   * @param code - The error code for which the description needs to be retrieved.
   * @returns The description of the error code if found, otherwise the input code.
   */
  getErrorDescription(code: string): string {
    const description = this.scriptErrors.find(item => item.code === code)?.description;
    return description ?? this.translate.instant(code);
  }
}
